import moment from 'moment'
import { LazyUserCell } from 'app/components/LazyUserCell'
import { cn } from 'app/lib/utils'
import { DateTile } from './DateTile'
import { LastTouch } from './useLastTouch'

/**
 * Who reached this contact last, and how, as a panel floating beside the
 * focus card. Today's touch is highlighted: that is the window where two
 * people working a shared list can still collide.
 */
export function LastActivityPanel({
  touch,
  loading,
  onView,
}: {
  touch?: LastTouch
  loading?: boolean
  onView?: () => void
}) {
  // Hold off until the read lands rather than flashing an empty state that
  // immediately corrects itself on every card.
  if (loading) return null
  const when = touch ? moment(touch.at) : undefined
  const today = !!when && when.isSame(moment(), `day`)
  const label = !touch
    ? undefined
    : touch.kind === `text`
      ? `Text message`
      : touch.kind === `follow-up`
        ? `Follow-up`
        : touch.kind === `call`
          ? touch.inbound
            ? `Inbound call`
            : `Outbound call`
          : (touch.rawType ?? `Activity`).replace(/([a-z])([A-Z])/g, `$1 $2`)
  // Inbound calls credit whoever picked up, which is not team attribution.
  const attributable = !(touch?.kind === `call` && touch.inbound)
  const actorNode =
    !touch || !attributable ? null : touch.actor ? (
      <>{touch.actor}</>
    ) : touch.actorUserId ? (
      <LazyUserCell userId={touch.actorUserId} />
    ) : null

  return (
    <aside
      className={cn(
        // In flow above the card, not hung off it with `absolute bottom-full`: hung, it rode up
        // behind the Card view's bar on any window short enough to need the room.
        `mb-4 hidden w-80 overflow-hidden rounded-2xl border bg-surface-raised shadow-elevation-4 lg:block`,
        today ? `border-amber-200` : `border-default`,
      )}
    >
      <div
        className={cn(
          `flex items-center justify-between gap-2 border-b px-4 py-2.5`,
          today ? `border-amber-200 bg-amber-500/10` : `border-subtle bg-surface-subtle`,
        )}
      >
        <span className={cn(`text-label-xs uppercase tracking-wide`, today ? `text-amber-800` : `text-subtle`)}>
          Last activity
        </span>
        <span className="flex items-center gap-2">
          {today && <span className="rounded bg-amber-500 px-2 py-0.5 text-label-xs text-on-color">Today</span>}
          {touch && onView && (
            <button type="button" onClick={onView} className="text-body-xs text-primary-600 transition hover:underline">
              View
            </button>
          )}
        </span>
      </div>

      <div className="p-4">
        {!touch || !when ? (
          <div>
            <p className="text-ui-sm text-default">Nothing yet</p>
            <p className="mt-1 text-body-xs text-subtle">No one has reached out to this contact.</p>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <DateTile date={touch.at} headClass={today ? `bg-amber-500` : `bg-fill-neutral`} />
            <div className="min-w-0">
              <p className="text-ui-sm text-strong">
                {label}
                {actorNode && <span className="font-normal text-subtle"> by {actorNode}</span>}
              </p>
              <p className="mt-1 text-body-xs text-subtle">
                {when.fromNow()} · {when.format(`h:mm A`)}
              </p>
            </div>
          </div>
        )}

        {touch?.snippet && (
          // The clamp lives on the inner element, not the padded bubble:
          // clamped with padding on the same node, a third line renders into
          // that padding and is sliced through rather than hidden.
          <div className="mt-3 rounded-xl bg-surface-subtle px-3 py-2.5">
            <p className="line-clamp-2 text-body-sm leading-relaxed text-default">
              {touch.snippet.replace(/\s+/g, ` `).trim()}
            </p>
          </div>
        )}
      </div>
    </aside>
  )
}
